/**
 * 白板状态仓库：维护页面、图元与本地绘制过程。
 */

import { create } from 'zustand'

import type { Client } from '../lib/client'
import { MsgType } from '../lib/messages'
import { USER_NONE } from '../lib/protocol'
import {
  DEFAULT_STYLE,
  type WBLockObj,
  type WBLockPage,
  type WBObjKind,
  type WBObject,
  type WBOpMsg,
  type WBPage,
  type WBPageRef,
  type WBPoint,
  type WBSnapshot,
  type WBStyle,
  type WBTool,
} from '../lib/whiteboard-types'

interface WBState {
  pages: WBPage[]
  currentPage: number
  tool: WBTool
  style: WBStyle

  /** 本地正在绘制、尚未拿到服务端对象号的图元。 */
  draft: WBObject | null
  /** 本地已提交但还没收到回执的图元，按提交序号索引。 */
  selectedIds: number[]

  bind: (client: Client) => () => void
  setTool: (t: WBTool) => void
  setStyle: (s: Partial<WBStyle>) => void
  gotoPage: (page: number) => void

  /** 开始画。不传 page 时用当前页；批注层不是当前页，必须显式传。 */
  beginDraw: (kind: WBObjKind, at: WBPoint, selfId: number, page?: number) => void
  extendDraw: (at: WBPoint) => void
  /** 结束画并发送。不传 page 时用 draft.page。 */
  endDraw: (client: Client, page?: number) => void
  cancelDraw: () => void

  addText: (client: Client, at: WBPoint, text: string, selfId: number) => void
  /** 橡皮擦。不传 page 时用当前页；批注层要显式传。 */
  eraseAt: (client: Client, at: WBPoint, selfId: number, page?: number) => void
  /** 选中某点最上层的图元；点空白处清空选择。additive 为真时追加。 */
  selectAt: (at: WBPoint, additive: boolean) => void
  /** 清空选择。 */
  clearSelection: () => void
  /** 删除当前选中的图元。 */
  deleteSelected: (client: Client) => void
  /** 把选中的图元整体平移。拖动过程中本地先动，松手才发给服务端 */
  moveSelected: (client: Client, delta: WBPoint) => void
  /** 缩放单个图元：把它的包围盒改成给定的框 */
  resizeObject: (client: Client, objId: number, box: [WBPoint, WBPoint]) => void
  /** 本地预览用的临时位移，不发网络 */
  dragOffset: WBPoint | null
  setDragOffset: (d: WBPoint | null) => void

  addPage: (client: Client) => void
  /** 清空一页。不传 page 时清当前页；批注层不是当前页，必须显式传。 */
  clearPage: (client: Client, page?: number) => void
  deletePage: (client: Client) => void
  lockPage: (client: Client, lock: boolean) => void
  /** 给当前页命名。导入课件后每页都只有页号，讲到哪一页全靠数。 */
  renamePage: (client: Client, title: string) => void
  /** 图元级锁定：锁住单个图元不让别人改，比锁整页细。 */
  lockObject: (client: Client, objId: number, lock: boolean) => void
}

/** 空白板的初始状态。 */
const emptyPages = (): WBPage[] => [{ index: 0, objects: [], lockBy: USER_NONE }]

/**
 * 把服务端送来的页规整一遍，保证 objects 一定是数组。
 *
 * 服务端已经保证不再下发 null（见 protocol.NewWBPage），这里是第二道闸门：
 * 缺了它，一个 null 就足以让整块白板在客户端抛异常，而服务端日志上
 * 干干净净什么都看不到 —— 这种崩法排查起来非常费劲，挡在入口最划算。
 */
/**
 * 共享批注层的页标题。服务端建这一页时用的就是这个字符串。
 *
 * 用标题识别而不是加一个新协议字段：批注层本质就是白板的一个普通页，
 * 加字段要动协议、快照、录制回放一整串；而标题本来就在快照里传下来。
 * 代价是老师不能给普通页起这个名 —— 页标题由 UI 里的「重命名」输入，
 * 撞上的概率可以接受，撞了也只是那一页不显示在导航里。
 */
export const ANNOT_PAGE_TITLE = '共享批注'

/** 是不是共享批注层。 */
export function isAnnotPage(p: WBPage | undefined): boolean {
  return p?.title === ANNOT_PAGE_TITLE
}

function normalizePages(pages: WBPage[] | undefined): WBPage[] {
  if (!pages?.length) return emptyPages()
  return pages.map((p) => ({ ...p, objects: p.objects ?? [] }))
}

export const useWhiteboardStore = create<WBState>((set, get) => ({
  pages: emptyPages(),
  currentPage: 0,
  tool: 'pen',
  style: { ...DEFAULT_STYLE },
  draft: null,
  selectedIds: [],

  /** 把服务端白板事件接到仓库上，返回退订函数。 */
  bind(client) {
    const offs: Array<() => void> = []

    // 进房即拿到全量白板 —— 否则中途进来的人看到空白板，
    // 且本地页号会与房间实际页号错位。
    offs.push(
      client.on<{ whiteboard?: WBSnapshot }>(MsgType.RoomJoined, (d) => {
        const wb = d.whiteboard
        set(() => ({
          pages: normalizePages(wb?.pages),
          currentPage: wb?.currentPage ?? 0,
          draft: null,
        }))
      }),
    )

    offs.push(
      client.on<WBSnapshot>(MsgType.WBSnapshot, (d) =>
        set(() => ({
          pages: normalizePages(d.pages),
          currentPage: d.currentPage ?? 0,
        })),
      ),
    )

    offs.push(
      client.on<WBOpMsg>(MsgType.WBOp, (d) => set((s) => ({ pages: applyOp(s.pages, d) }))),
    )

    offs.push(
      client.on<WBPageRef>(MsgType.WBPageAdd, (d) =>
        set((s) => {
          if (s.pages.some((p) => p.index === d.page)) return {}
          return {
            pages: [...s.pages, { index: d.page, title: d.title, objects: [], lockBy: USER_NONE }],
          }
        }),
      ),
    )

    // 删页。以前前端不订阅这条：普通「删除本页」时服务端紧跟着发一份全量快照，
    // 删页消息丢了也被快照兜住，看不出来。但共享结束删批注层时（room/share.go）
    // 只发删页、不发快照，于是批注层在各端永远留着，变成一页莫名的涂鸦。
    // 删完按服务端同样的规则重排页号（room/whiteboard_page.go），与后续快照一致。
    offs.push(
      client.on<WBPageRef>(MsgType.WBPageDel, (d) =>
        set((s) => {
          if (!s.pages.some((p) => p.index === d.page)) return {}
          const pages = s.pages
            .filter((p) => p.index !== d.page)
            .map((p, i) => (p.index === i ? p : { ...p, index: i, objects: p.objects.map((o) => ({ ...o, page: i })) }))
          return { pages, currentPage: Math.min(s.currentPage, Math.max(0, pages.length - 1)) }
        }),
      ),
    )

    offs.push(
      client.on<WBPageRef>(MsgType.WBPageClear, (d) =>
        set((s) => ({
          pages: s.pages.map((p) => (p.index === d.page ? { ...p, objects: [] } : p)),
        })),
      ),
    )

    offs.push(
      client.on<WBPageRef>(MsgType.WBPageTitle, (d) =>
        set((s) => ({
          pages: s.pages.map((p) => (p.index === d.page ? { ...p, title: d.title } : p)),
        })),
      ),
    )

    offs.push(
      client.on<WBPageRef>(MsgType.WBPageGoto, (d) => set(() => ({ currentPage: d.page }))),
    )

    offs.push(
      client.on<WBLockPage>(MsgType.WBLockPage, (d) =>
        set((s) => ({
          pages: s.pages.map((p) =>
            p.index === d.page ? { ...p, lockBy: d.lock ? 0 : USER_NONE } : p,
          ),
        })),
      ),
    )

    offs.push(
      client.on<WBLockObj>(MsgType.WBLockObj, (d) =>
        set((s) => ({
          pages: s.pages.map((p) => ({
            ...p,
            objects: p.objects.map((o) =>
              o.id === d.objId ? { ...o, lockBy: d.lock ? 0 : USER_NONE } : o,
            ),
          })),
        })),
      ),
    )

    // 图片等大块数据落地后，服务端回传 URL。
    offs.push(
      client.on<{ objId: number; url: string }>(MsgType.WBBlobEnd, (d) =>
        set((s) => ({
          pages: s.pages.map((p) => ({
            ...p,
            objects: p.objects.map((o) => (o.id === d.objId ? { ...o, blobUrl: d.url } : o)),
          })),
        })),
      ),
    )

    // 删页会导致页号整体位移，服务端随后补发全量快照，这里无需单独处理。
    return () => offs.forEach((f) => f())
  },

  setTool(t) {
    set(() => ({ tool: t, draft: null }))
  },

  setStyle(s) {
    set((prev) => ({ style: { ...prev.style, ...s } }))
  },

  gotoPage(page) {
    // 选择必须跟着页一起清掉：图元号是全房间唯一的，留着上一页的选择
    // 再按删除，删的就是另一页上的东西 —— 而且当事人看不见，
    // 只会觉得自己的板书凭空少了一块。
    set(() => ({ currentPage: page, draft: null, selectedIds: [] }))
  },

  beginDraw(kind, at, selfId, page) {
    const { style, currentPage } = get()
    const targetPage = page ?? currentPage
    set(() => ({
      draft: {
        id: 0,
        kind,
        page: targetPage,
        ownerId: selfId,
        points: [at, at],
        style: { ...style },
        z: 0,
        time: Date.now(),
      },
    }))
  },

  extendDraw(at) {
    set((s) => {
      if (!s.draft) return {}
      const pts = s.draft.points ?? []
      // 自由笔追加点；几何图形只更新第二个点（包围盒的对角）。
      const next =
        s.draft.kind === 'pen' ? [...pts, at] : [pts[0] ?? at, at]
      return { draft: { ...s.draft, points: next } }
    })
  },

  endDraw(client, page) {
    const { draft } = get()
    set(() => ({ draft: null }))
    if (!draft) return
    const pts = draft.points ?? []
    if (pts.length < 2) return
    // 点击没拖动的话不产生图元，避免满屏小点。
    if (draft.kind !== 'pen' && isSamePoint(pts[0]!, pts[1]!)) return

    const targetPage = page ?? draft.page
    client.send(MsgType.WBOp, {
      page: targetPage,
      op: 'add',
      obj: { ...draft, page: targetPage },
    } satisfies WBOpMsg)
  },

  cancelDraw() {
    set(() => ({ draft: null }))
  },

  addText(client, at, text, selfId) {
    if (!text.trim()) return
    const { style, currentPage } = get()
    client.send(MsgType.WBOp, {
      page: currentPage,
      op: 'add',
      obj: {
        id: 0,
        kind: 'text',
        page: currentPage,
        ownerId: selfId,
        points: [at],
        text,
        style: { ...style },
        z: 0,
        time: Date.now(),
      },
    } satisfies WBOpMsg)
  },

  eraseAt(client, at, _selfId, pageIdx) {
    const { pages, currentPage } = get()
    const targetPage = pageIdx ?? currentPage
    const page = pages.find((p) => p.index === targetPage)
    if (!page) return
    // 命中测试从上往下找，只删最上面那个，避免一次抹掉一叠。
    const hit = [...page.objects].reverse().find((o) => hitTest(o, at))
    if (!hit) return
    client.send(MsgType.WBOp, {
      page: targetPage,
      op: 'delete',
      objIds: [hit.id],
    } satisfies WBOpMsg)
  },

  selectAt(at, additive) {
    const { pages, currentPage, selectedIds } = get()
    const page = pages.find((p) => p.index === currentPage)
    if (!page) return
    // 与橡皮一致：从上往下找，只取最上面那个。
    const hit = [...page.objects].reverse().find((o) => hitTest(o, at))
    if (!hit) {
      set({ selectedIds: additive ? selectedIds : [] })
      return
    }
    if (!additive) {
      set({ selectedIds: [hit.id] })
      return
    }
    // 追加模式下再点一次是取消选中，这是画图工具的通行做法。
    set({
      selectedIds: selectedIds.includes(hit.id)
        ? selectedIds.filter((id) => id !== hit.id)
        : [...selectedIds, hit.id],
    })
  },

  clearSelection() {
    set({ selectedIds: [] })
  },

  deleteSelected(client) {
    const { selectedIds, currentPage } = get()
    if (!selectedIds.length) return
    client.send(MsgType.WBOp, {
      page: currentPage,
      op: 'delete',
      objIds: selectedIds,
    } satisfies WBOpMsg)
    set({ selectedIds: [] })
  },

  dragOffset: null,
  setDragOffset(dragOffset) {
    set({ dragOffset })
  },

  moveSelected(client, delta) {
    const { selectedIds, currentPage } = get()
    if (!selectedIds.length) return
    // 几乎没动就别发 —— 点一下选中时手会抖，抖出来的一堆 move
    // 既占带宽，也会把「谁改过这个图元」的记录搅乱。
    if (Math.abs(delta.x) < 0.002 && Math.abs(delta.y) < 0.002) return
    client.send(MsgType.WBOp, {
      page: currentPage,
      op: 'move',
      objIds: selectedIds,
      delta,
      time: Date.now(),
    } satisfies WBOpMsg)
  },

  resizeObject(client, objId, box) {
    const { pages, currentPage } = get()
    const obj = pages.find((p) => p.index === currentPage)?.objects.find((o) => o.id === objId)
    if (!obj) return
    // 缩放走 modify 而不是 move：move 只能整体平移，改不了大小。
    // 服务端的 modify 会整份换掉 points，所以这里要把完整的对象发过去。
    client.send(MsgType.WBOp, {
      page: currentPage,
      op: 'modify',
      objId,
      obj: { ...obj, points: [box[0], box[1]] },
      time: Date.now(),
    } satisfies WBOpMsg)
  },

  addPage(client) {
    client.send(MsgType.WBPageAdd, { page: 0, title: '' } satisfies WBPageRef)
  },

  clearPage(client, page) {
    // 默认清当前页；共享批注要清的是批注层那一页，而它不是 currentPage ——
    // 不传参数会把老师正在讲的课件页清掉。
    client.send(MsgType.WBPageClear, { page: page ?? get().currentPage } satisfies WBPageRef)
  },

  deletePage(client) {
    client.send(MsgType.WBPageDel, { page: get().currentPage } satisfies WBPageRef)
  },

  lockPage(client, lock) {
    client.send(MsgType.WBLockPage, { page: get().currentPage, lock } satisfies WBLockPage)
  },

  renamePage(client, title) {
    client.send(MsgType.WBPageTitle, {
      page: get().currentPage,
      title: title.trim(),
    } satisfies WBPageRef)
  },

  lockObject(client, objId, lock) {
    client.send(MsgType.WBLockObj, { objId, lock } satisfies WBLockObj)
  },
}))

/** 把一条白板操作应用到本地页面。 */
function applyOp(pages: WBPage[], op: WBOpMsg): WBPage[] {
  switch (op.op) {
    case 'add': {
      if (!op.obj) return pages
      const obj = op.obj
      return pages.map((p) =>
        p.index === obj.page ? { ...p, objects: [...p.objects, obj] } : p,
      )
    }
    case 'modify': {
      if (!op.obj) return pages
      const obj = op.obj
      return pages.map((p) => ({
        ...p,
        objects: p.objects.map((o) => (o.id === obj.id ? { ...o, ...obj } : o)),
      }))
    }
    case 'appendPt': {
      if (!op.objId || !op.points?.length) return pages
      return pages.map((p) => ({
        ...p,
        objects: p.objects.map((o) =>
          o.id === op.objId ? { ...o, points: [...(o.points ?? []), ...op.points!] } : o,
        ),
      }))
    }
    case 'move': {
      if (!op.delta || !op.objIds?.length) return pages
      const ids = new Set(op.objIds)
      const d = op.delta
      return pages.map((p) => ({
        ...p,
        objects: p.objects.map((o) =>
          ids.has(o.id)
            ? { ...o, points: (o.points ?? []).map((pt) => ({ x: pt.x + d.x, y: pt.y + d.y })) }
            : o,
        ),
      }))
    }
    case 'delete': {
      if (!op.objIds?.length) return pages
      const ids = new Set(op.objIds)
      return pages.map((p) => ({ ...p, objects: p.objects.filter((o) => !ids.has(o.id)) }))
    }
    default:
      return pages
  }
}

/** 判断两点是否几乎重合（归一化坐标下的阈值）。 */
function isSamePoint(a: WBPoint, b: WBPoint): boolean {
  return Math.abs(a.x - b.x) < 0.002 && Math.abs(a.y - b.y) < 0.002
}

/** 粗略的命中测试，供橡皮擦使用。 */
function hitTest(o: WBObject, at: WBPoint): boolean {
  const pts = o.points ?? []
  if (!pts.length) return false
  const tol = 0.015

  if (o.kind === 'pen' || o.kind === 'poly' || o.kind === 'line') {
    return pts.some((p) => Math.hypot(p.x - at.x, p.y - at.y) < tol)
  }
  if (o.kind === 'text') {
    const p = pts[0]!
    const h = o.style.fontSize ?? 0.03
    return at.x >= p.x - tol && at.y >= p.y - h - tol && at.y <= p.y + tol && at.x <= p.x + 0.4
  }
  // 矩形/椭圆/图片按包围盒判定。
  const [a, b] = [pts[0]!, pts[1] ?? pts[0]!]
  const minX = Math.min(a.x, b.x) - tol
  const maxX = Math.max(a.x, b.x) + tol
  const minY = Math.min(a.y, b.y) - tol
  const maxY = Math.max(a.y, b.y) + tol
  return at.x >= minX && at.x <= maxX && at.y >= minY && at.y <= maxY
}

declare global {
  interface Window {
    /** 测试探针：读白板全部页（含共享批注层）。
     *  批注同步只能这样验 —— 判据必须是「学员端拿到了对象」，
     *  而画布上有笔迹只证明本地画上了，证明不了同步。 */
    __wbPages?: () => WBPage[]
  }
}
if (typeof window !== 'undefined') {
  window.__wbPages = () => useWhiteboardStore.getState().pages
}
